import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, DatesSetArg } from '@fullcalendar/core';
import { Calendar } from 'app/model/calendar';
import { UserCalendarStats } from 'app/model/userCalendarStats';
import { HolidayService } from 'app/shared/services/data/holiday.service';
import { ScreenService } from 'app/shared/services/data/screen.service';
import { UserCalendarStatsViewService } from 'app/shared/services/data/user-calendar-stats/user-calendar-stats-view.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, map } from 'rxjs/operators';
import { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

@Component({
  selector: 'app-user-calendar-stats',
  templateUrl: './user-calendar-stats.component.html',
  styleUrls: ['./user-calendar-stats.component.scss']
})
export class UserCalendarStatsComponent implements OnInit, OnChanges {
  private isMobile: boolean

  private isInitialized: boolean

  public calendars: Calendar[]

  public stats: UserCalendarStats

  public calendarOptions: CalendarOptions

  @Input() private employeeId: number

  @ViewChild(FullCalendarComponent) private calendar: FullCalendarComponent

  /**
    * Constructor
    *
    * @param {UserCalendarStatsViewService} userCalendarStatsViewService
    * @param {HolidayService} holidayService
    * @param {NgxSpinnerService} spinner
    * @param {ScreenService} screenService
    * 
  */
  constructor(
    private userCalendarStatsViewService: UserCalendarStatsViewService,
    private holidayService: HolidayService,
    private spinner: NgxSpinnerService,
    private screenService: ScreenService
  ) { }

  ngOnInit(): void {
    this.initializeValues()
  }

  ngOnChanges(changes: SimpleChanges): void {
    const employeeId = changes['employeeId']

    if (employeeId && employeeId.currentValue) this.getUserCalendarStatsView(new Date().getFullYear())
  }

  private initializeValues(): void {
    this.isInitialized = false

    this.screenServiceSubscribe()
  }

  private screenServiceSubscribe(): void {
    this.screenService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile

      if (!this.calendar) return

      const calendarApi = this.calendar.getApi()

      calendarApi.setOption('eventContent', (arg) => this.userCalendarStatsViewService.getCustomEventsContent(arg, isMobile))

      calendarApi.render()
    })
  }

  private getUserCalendarStatsView(year: number): void {
    this.spinner.show(undefined, {})

    this.holidayService.getHolidays(year)
      .pipe(
        catchError(() => this.handleHolidaysError(year)),
        switchMap((holidays) => this.userCalendarStatsViewService.getUserCalendarStatsView(this.employeeId, year)
          .pipe(
            map((data) => ({ holidays, data })),
            finalize(() => this.spinner.hide())
          )
        )
      )
      .subscribe({
        next: ({ holidays, data }) => {
          this.calendars = data.calendars

          this.stats = data.stats

          this.updateEventOfUserCalendar(holidays, year)
        },
        error: () => {
          const alertOptions: SweetAlertOptions = {
            title: '¡Error!',
            html: `Ha ocurrido un error al intentar cargar las estadísticas del calendario del usuario. <br><br> Póngase en contacto con un administrador.`,
            icon: 'error',
            customClass: {
              confirmButton: 'btn red-bg text-white ml-1'
            },
          }

          this.fireSwalAlert(alertOptions)
        }
      })
  }

  private updateEventOfUserCalendar(holidays: string[], year: number): void {
    if (!this.calendar) {
      this.makeCalendarOptions(holidays, year)

      return
    }

    const api = this.calendar.getApi()

    const events = this.userCalendarStatsViewService.makeEvents(this.calendars, holidays, year)

    api.removeAllEvents()
    api.addEventSource(events)
    api.refetchEvents()
  }

  private makeCalendarOptions(holidays: string[], year: number): void {
    const calendarOptions = this.userCalendarStatsViewService.makeCalendarOptions(this.calendars, holidays, year, this.isMobile)
    calendarOptions.datesSet = (arg) => this.refreshUserCalendarStatsView(arg)

    this.calendarOptions = calendarOptions
  }

  private refreshUserCalendarStatsView(arg: DatesSetArg): void {
    if (!this.isInitialized) {
      this.isInitialized = true

      return
    }

    const calendarYear = arg.start.getFullYear()

    this.getUserCalendarStatsView(calendarYear)
  }

  private handleHolidaysError(year: number): Observable<never> {
    this.spinner.hide()

    const htmlText = `Ha ocurrido un error al intentar cargar los festivos del año ${year}. <br><br> Póngase en contacto con un administrador.`

    const alertOptions: SweetAlertOptions = {
      title: '¡Error!',
      html: htmlText,
      icon: 'error',
      customClass: {
        confirmButton: 'btn red-bg text-white ml-1'
      },
    }

    this.fireSwalAlert(alertOptions)

    return throwError(() => new Error())
  }

  private fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return this.userCalendarStatsViewService.fireSwalAlert(options)
  }
}
